/**
 * frameMeasure.js — ЗАМЕР КАДРА и СРЕДНЯЯ ЯРКОСТЬ. Одно место на всю программу.
 * Mikhmel Cornerstone4D DICOM Viewer Pro — 10.19.17
 *
 * ПОВОД. 16.08.2026 Андрей Вячеславович прислал числа из плашки замера со своей
 * машины (RTX 4060, WebGPU): **слой 0,0% непустых пикселей и яркость 0,0 против
 * 36,6% и 29,3 у окна**. У меня в песочнице на WebGL2 те же числа были 15,6% и
 * 4,4 — то есть картинка есть, но задавлена пост-обработкой. Ноль — другое.
 *
 * И тут выяснилось, что мой замер НЕ УМЕЕТ РАЗЛИЧИТЬ два разных случая:
 *
 *   1) кадр действительно чёрный;
 *   2) прочитать холст не удалось (для WebGPU-холста снимок через drawImage
 *      может вернуться пустым — содержимое swap-chain живёт не так, как у
 *      WebGL с preserveDrawingBuffer).
 *
 * В обоих случаях старый код печатал «0,0% и яркость 0,0» и снимал слой. Если
 * верен случай (2), я снимаю с экрана РАБОТАЮЩИЙ рендер и вру пользователю
 * числами. Это ровно то, за что я сам себя ругал в 10.19.16: «пропущенная
 * проверка на экране неотличима от пройденной».
 *
 * ПРАВИЛО. Замер обязан возвращать ТРИ разных ответа: «есть числа», «холст
 * пуст», «замерить не удалось» — и последнее НЕ является поводом снимать слой.
 *
 * Здесь же считается ось цветокора (средняя яркость содержимого кадра): она
 * нужна шейдерам, чтобы контраст поворачивался вокруг середины ИЗОБРАЖЕНИЯ, а
 * не вокруг константы 0,5, которая для объёмного рендера КТ лежит в 80 раз выше
 * любого реального пикселя.
 */

import { log } from '../utils/logger.js';

/** Порог «непустого» пикселя по среднему из RGB, 0…255. */
export const ПОРОГ_ПУСТОТЫ_ПИКСЕЛЯ = 12;

/** Ось цветокора по умолчанию: средне-серый 18% в линейном пространстве. */
export const ОСЬ_ПО_УМОЛЧАНИЮ = 0.18;

/** sRGB 0…1 → линейное 0…1 (та же кривая, что в шейдерах). */
export function вЛинейное(c) {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

let _буфер = null;

/** Один переиспользуемый холст на все замеры: замер не должен мусорить. */
function малыйХолст(w, h) {
  if (!_буфер) {
    if (typeof document === 'undefined') return null;
    _буфер = document.createElement('canvas');
  }
  if (_буфер.width !== w || _буфер.height !== h) { _буфер.width = w; _буфер.height = h; }
  return _буфер;
}

/**
 * Замер холста: доля непустых пикселей, средняя яркость по всему кадру и
 * средняя яркость ПО СОДЕРЖИМОМУ (только непустые пиксели).
 *
 * @returns {{ок: true, доляНепустых: number, яркость: number, яркостьСодержимого: number,
 *            rgb: number[], размер: string} |
 *           {ок: false, причина: string}}
 */
export function замерХолста(холст, сторона = 96) {
  if (!холст) return { ок: false, причина: 'холста нет' };
  if (!(холст.width > 0) || !(холст.height > 0)) {
    return { ок: false, причина: `холст нулевого размера (${холст.width}×${холст.height})` };
  }
  try {
    const k = сторона / Math.max(холст.width, холст.height);
    const w = Math.max(8, Math.round(холст.width * k));
    const h = Math.max(8, Math.round(холст.height * k));
    const мал = малыйХолст(w, h);
    if (!мал) return { ок: false, причина: 'нет document для холста замера' };
    const ctx = мал.getContext('2d', { willReadFrequently: true });
    if (!ctx) return { ок: false, причина: 'не выдан контекст 2d' };
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(холст, 0, 0, w, h);
    const d = ctx.getImageData(0, 0, w, h).data;

    let непустых = 0, сумма = 0, суммаСодержимого = 0;
    let r = 0, g = 0, b = 0, альфаНоль = 0;
    for (let i = 0; i < d.length; i += 4) {
      const y = (d[i] + d[i + 1] + d[i + 2]) / 3;
      сумма += y;
      if (d[i + 3] === 0) альфаНоль++;
      if (y > ПОРОГ_ПУСТОТЫ_ПИКСЕЛЯ) { непустых++; суммаСодержимого += y; r += d[i]; g += d[i + 1]; b += d[i + 2]; }
    }
    const всего = d.length / 4;

    // ── ГЛАВНОЕ РАЗЛИЧЕНИЕ, 10.19.17 ────────────────────────────────────────
    // Полностью прозрачный снимок — это НЕ чёрный кадр, это очищенный буфер
    // отрисовки. WebGL без preserveDrawingBuffer очищает его после вывода на
    // экран, а у WebGPU содержимое swap-chain и вовсе живёт лишь до вывода.
    // На экране при этом всё в порядке — снимок просто опоздал.
    //
    // Композит рендерера всегда пишет альфу 1.0, поэтому «все пиксели
    // прозрачные» однозначно означает «снимок не состоялся». Именно этот случай
    // раньше печатался как «слой 0,0% непустых пикселей и яркость 0,0» — и по
    // этим числам с экрана снимался работающий рендер.
    if (всего > 0 && альфаНоль / всего > 0.999) {
      return {
        ок: false,
        причина: 'снимок холста полностью прозрачный — буфер отрисовки уже очищен '
          + '(это не чёрный кадр, а опоздавший замер)',
        доляПрозрачных: 1,
      };
    }

    return {
      ок: true,
      доляНепустых: непустых / всего,
      яркость: сумма / всего,
      яркостьСодержимого: непустых > 0 ? суммаСодержимого / непустых : 0,
      доляПрозрачных: альфаНоль / всего,
      rgb: непустых > 0 ? [Math.round(r / непустых), Math.round(g / непустых), Math.round(b / непустых)] : [0, 0, 0],
      размер: `${холст.width}×${холст.height}`,
    };
  } catch (e) {
    // Именно СЮДА попадает «не удалось замерить»: tainted canvas, потерянный
    // контекст, холст без содержимого. Это НЕ «кадр пуст».
    return { ок: false, причина: `снимок холста не удался: ${e.message}` };
  }
}

/**
 * Статистика по СЫРЫМ байтам, снятым с GPU (copyTextureToBuffer / readPixels).
 *
 * Здесь же разбирается порядок каналов: формат холста WebGPU обычно
 * `bgra8unorm`, и если считать его как RGBA, «синий» и «красный» поменяются
 * местами — в отчёте это выглядело бы как цветовой сдвиг, которого нет.
 *
 * @param {{данные: Uint8Array, w: number, h: number, bpr?: number, формат?: string}} снимок
 */
export function статистикаПоБайтам(снимок) {
  const { данные, w, h } = снимок || {};
  if (!данные || !(w > 0) || !(h > 0)) return { ок: false, причина: 'снимок пуст' };
  const bpr = снимок.bpr || w * 4;
  const bgr = String(снимок.формат || '').startsWith('bgra');
  let непустых = 0, сумма = 0, суммаСодержимого = 0, r = 0, g = 0, b = 0, альфаНоль = 0;
  const всего = w * h;
  for (let y = 0; y < h; y++) {
    const строка = y * bpr;
    for (let x = 0; x < w; x++) {
      const i = строка + x * 4;
      const к1 = данные[i], к2 = данные[i + 1], к3 = данные[i + 2], a = данные[i + 3];
      const кр = bgr ? к3 : к1;
      const си = bgr ? к1 : к3;
      const я = (кр + к2 + си) / 3;
      сумма += я;
      if (a === 0) альфаНоль++;
      if (я > ПОРОГ_ПУСТОТЫ_ПИКСЕЛЯ) { непустых++; суммаСодержимого += я; r += кр; g += к2; b += си; }
    }
  }
  return {
    ок: true,
    доляНепустых: непустых / всего,
    яркость: сумма / всего,
    яркостьСодержимого: непустых > 0 ? суммаСодержимого / непустых : 0,
    доляПрозрачных: альфаНоль / всего,
    rgb: непустых > 0 ? [Math.round(r / непустых), Math.round(g / непустых), Math.round(b / непустых)] : [0, 0, 0],
    размер: `${w}×${h}`,
  };
}

/**
 * Ось цветокора по кадру: средняя ЛИНЕЙНАЯ яркость содержимого.
 *
 * Почему по содержимому, а не по всему кадру: объёмный рендер — это анатомия на
 * чёрном фоне. Средняя по всему кадру у КТ головы около 0,006 в линейном
 * пространстве, и ось, посаженная туда, задирала бы контраст фона. Средняя по
 * непустым пикселям — это середина ТКАНИ, вокруг неё контраст и должен
 * поворачиваться: тёмное темнее, светлое светлее, средняя яркость на месте.
 */
export function осьПоКадру(холст) {
  const з = замерХолста(холст, 64);
  if (!з.ок || !(з.яркостьСодержимого > 0)) return { ось: ОСЬ_ПО_УМОЛЧАНИЮ, измерено: false, причина: з.причина || 'в кадре нет содержимого' };
  const ось = вЛинейное(з.яркостьСодержимого / 255);
  return { ось: Math.min(0.5, Math.max(0.002, ось)), измерено: true, яркостьСодержимого: з.яркостьСодержимого };
}

/**
 * Ось с прореживанием: считать каждый кадр незачем — картинка меняется
 * медленнее, чем 60 раз в секунду, а drawImage+getImageData не бесплатны.
 */
export class ОсьКадра {
  constructor(каждыеКадров = 15) {
    this._каждые = Math.max(1, каждыеКадров | 0);
    this._ось = ОСЬ_ПО_УМОЛЧАНИЮ;
    this._измерено = false;
    this._счёт = -1;
  }

  /** @returns {number} ось для шейдера (линейная яркость) */
  обновить(холст, принудительно = false) {
    this._счёт++;
    if (!принудительно && this._счёт % this._каждые !== 0) return this._ось;
    try {
      const р = осьПоКадру(холст);
      this._ось = р.ось;
      this._измерено = р.измерено;
    } catch (e) {
      log.debug('[Кино/ось]', e.message);
    }
    return this._ось;
  }

  значение() { return this._ось; }
  измерено() { return this._измерено; }
}

export default { замерХолста, статистикаПоБайтам, осьПоКадру, ОсьКадра, вЛинейное, ОСЬ_ПО_УМОЛЧАНИЮ, ПОРОГ_ПУСТОТЫ_ПИКСЕЛЯ };
